import { Cell, focusBoxShadow, FormControl, Grid, Theme, useStyles, VFlow } from 'bold-ui'
import { useFormControl, UseFormControlProps, useRovingTabIndex } from 'bold-ui/lib/hooks'
import {
  createRichTextEditor,
  ErrorField,
  FieldStatus,
  isRichTextValueEmpty,
  UseFieldProps,
  useFieldStatus,
  usePecField,
} from 'components/form'
import { DraftInlineStyle, EditorState } from 'draft-js'
import React, { CSSProperties, forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import RichTextEditor from 'react-rte'

import { getFieldError } from '../util'

export interface RichtextReference {
  handleReset: (initialValue?: string) => void
}

export interface RichTextProps extends UseFieldProps<string>, UseFormControlProps {
  maxLength: number
  height?: number
  placeholder?: string
  alert?: React.ReactNode
  removeNonBreakingSpaces?: boolean
  autofocus?: boolean
  controlModifiedStatus?: boolean
  dataTestId?: string
}

const RichTextFieldComponent = forwardRef((props: Readonly<RichTextProps>, ref: React.Ref<RichtextReference>) => {
  const {
    maxLength,
    height,
    alert,
    removeNonBreakingSpaces = false,
    autofocus = true,
    controlModifiedStatus = false,
    dataTestId,
    ...rest
  } = props

  const isInternalUpdateRef = useRef(false)
  const { input, meta, tools } = usePecField({
    parse: (value: string): string => (removeNonBreakingSpaces ? value.removeNonBreakingSpace() : value),
    ...rest,
  })

  const error = getFieldError(meta)
  const { getFormControlProps } = useFormControl(props)

  const [editorValueState, setEditorValueState] = useState(createRichTextEditor(input.value))

  const { status, showChangeStatus } = useFieldStatus({
    initialValue: meta.initial,
    currentValue: input.value,
    dirty: meta.dirty,
    controlModifiedStatus,
    isValueEmpty: isRichTextValueEmpty,
  })

  const qntdCharacters = useMemo(
    () => JSON.parse(editorValueState.toString('raw')).blocks.reduce((total, block) => total + block.text.length, 0),
    [editorValueState]
  )

  const { classes } = useStyles(createStyles, {
    height,
    error,
    meta,
    maxLength,
    qntdCharacters,
    showChangeStatus,
  })

  const rootRef = useRovingTabIndex({
    getItems: (root) => Array.from(root.querySelectorAll('button')),
  })

  const toolbarConfig = useMemo(() => {
    const config = {
      display: ['INLINE_STYLE_BUTTONS', 'BLOCK_TYPE_BUTTONS'],
      INLINE_STYLE_BUTTONS: [
        { label: 'Negrito', style: 'BOLD' },
        { label: 'Itálico', style: 'ITALIC' },
        { label: 'Sublinhado', style: 'UNDERLINE' },
        { label: 'Tachado', style: 'STRIKETHROUGH' },
      ],
      BLOCK_TYPE_BUTTONS: [{ label: 'Citação', style: 'blockquote' }],
    }

    if (!showChangeStatus) {
      config.display.push('HISTORY_BUTTONS')
      config['HISTORY_BUTTONS'] = { undo: { label: 'Desfazer' }, redo: { label: 'Refazer' } }
    }

    return config
  }, [showChangeStatus])

  const pendingInlineStyleOverrideRef = useRef<DraftInlineStyle | null>(null)

  const handleOnChange = (value: RichTextEditor) => {
    let editorState = value.getEditorState()
    const override = editorState.getInlineStyleOverride()

    if (override) {
      pendingInlineStyleOverrideRef.current = override
    } else if (pendingInlineStyleOverrideRef.current && editorState.getSelection().isCollapsed()) {
      editorState = EditorState.setInlineStyleOverride(editorState, pendingInlineStyleOverrideRef.current)
      value = value.setEditorState(editorState)
      pendingInlineStyleOverrideRef.current = null
    }

    isInternalUpdateRef.current = true
    setEditorValueState(value)

    if (isUndoOrRedo(value)) {
      input.onChange(value.toString('html'))
    }
  }

  const handleReset = (initialValue?: string) => handleOnChange(createRichTextEditor(initialValue))

  useEffect(() => {
    if (isInternalUpdateRef.current) {
      isInternalUpdateRef.current = false
    } else {
      setEditorValueState(createRichTextEditor(input.value))
    }
  }, [input.value])

  useEffect(() => {
    const container = rootRef.current?.parentElement
    if (!container) {
      return
    }

    const handleFocusOut = (event: FocusEvent) => {
      if (container.contains(event.relatedTarget as Node)) {
        return
      }

      input.onChange(editorValueState.toString('html'))
      input.onBlur()
    }

    container.addEventListener('focusout', handleFocusOut)
    return () => container.removeEventListener('focusout', handleFocusOut)
  }, [rootRef, editorValueState, input])

  useImperativeHandle(
    ref,
    (): RichtextReference => ({
      handleReset,
    })
  )

  return (
    <FormControl {...getFormControlProps()}>
      <VFlow style={classes.container} vSpacing={0.5} data-testid={dataTestId}>
        {alert}
        <RichTextEditor
          {...rest}
          value={editorValueState}
          onChange={handleOnChange}
          onFocus={input.onFocus}
          toolbarConfig={toolbarConfig}
          className={classes.root}
          toolbarClassName={classes.toolbar}
          editorClassName={classes.editor}
          ref={richTextToolbarRef(rootRef)}
          autofocus={autofocus}
        />
        <Grid justifyContent='space-between'>
          <Cell>
            <ErrorField name={props.name} />
          </Cell>
          <Cell>
            <div className={classes.footer}>
              {qntdCharacters}/{maxLength} caracteres
            </div>
          </Cell>
        </Grid>

        {showChangeStatus && <FieldStatus status={status} handleReset={tools.resetToInitialValue} />}
      </VFlow>
    </FormControl>
  )
})

const createStyles = (theme: Theme, { height, error, meta, maxLength, qntdCharacters, showChangeStatus }) => {
  const errorColor = meta.active ? theme.pallete.gray.c80 : theme.pallete.status.danger.main
  return {
    ...(showChangeStatus && {
      container: {
        border: `1px solid ${theme.pallete.gray.c80}`,
        borderRadius: '2px',
        padding: '1rem 1rem 0.5rem 1rem',
      } as CSSProperties,
    }),
    root: {
      backgroundColor: `${theme.pallete.surface.main} !important`,
      color: theme.pallete.text.main,
      border: `0.063rem solid ${error ? errorColor : theme.pallete.gray.c60} !important`,
      borderRadius: `${theme.radius.input}px !important`,
      boxShadow: meta.active && focusBoxShadow(theme, error ? 'danger' : 'primary'),
      transitionProperty: 'box-shadow',
      transitionDuration: '.2s',
    },
    toolbar: {
      display: 'flex',
      margin: '0.313rem 0 0.313rem',
      padding: '0.25rem 0 0.125rem !important',
      '&>div': {
        margin: 0,
      },
      ...(!showChangeStatus && {
        '&>div:last-child': {
          width: '100%',
          textAlign: 'end',
        },
      }),
      '.ButtonWrap__root___1EO_R': {
        zIndex: 8,
      },
      button: {
        background: 'none',
        border: 0,
        borderRadius: `${theme.radius.button}px !important`,
        margin: '0 0.625rem 0 0 !important',
        paddingTop: '4px',
        '&.IconButton__isActive___2Ey8p': {
          backgroundColor: theme.pallete.gray.c80,
          boxShadow: theme.shadows.inner[10],
        },
        '&:not(:disabled):hover': {
          backgroundColor: theme.pallete.gray.c90,
        },
        '&:focus': {
          boxShadow: `inset 0 0 0 2px ${theme.pallete.primary.main}`,
          outline: 0,
        },
        '&:active': {
          boxShadow: `inset 0 0 0 2px ${theme.pallete.primary.main}`,
        },
      },
    } as CSSProperties,
    editor: {
      minHeight: `${height | 4}rem`,
      margin: '0 0.563rem 0.125rem 0.188rem',
      wordBreak: 'break-all',
      fontFamily: `${theme.typography.fontFamily}`,
      fontSize: `${theme.typography.sizes.text} !important`,
      lineHeight: '1rem',
      '::placeholder': { color: `${theme.pallete.primary.main}` },
      '&>div>div>div': {
        padding: '0.25rem 0.5rem 0.5rem 0.5rem !important',
      },
      '.public-DraftEditor-content': {
        resize: 'vertical',
        minHeight: `${height | 4}rem`,
      },
    } as CSSProperties,
    footer: {
      color: `${qntdCharacters > maxLength ? theme.pallete.status.danger.main : theme.pallete.gray.c40}`,
    } as CSSProperties,
  }
}

function richTextToolbarRef(rootRef: React.MutableRefObject<any>) {
  return (elem) => {
    if (elem) rootRef.current = elem.editor.refs.editorContainer.parentNode.parentNode.previousSibling
    return true
  }
}

const isUndoOrRedo = (value: RichTextEditor) => {
  const type = value.getEditorState().getLastChangeType()
  return type === 'undo' || type === 'redo'
}

export const RichTextField = React.memo(RichTextFieldComponent)
